/**
 * Mobile pattern for the 3-pane rework (<768px): sticky app-style PILL tabs —
 * Photos › Configure › Cart(N) — over a single scrolling pane. Chevrons
 * between the pills suggest the flow direction; the active pill is solid
 * black with white text, inactive pills are outlined so they read as
 * tappable. The running total + the guided primary action live in Chrome's
 * sticky mobile footer (see mobileCta.ts).
 */

import { Fragment, type ReactNode } from 'react';

export type SpTab = 'photos' | 'configure' | 'cart';

export interface MobileTabsProps {
  tab: SpTab;
  onTab: (t: SpTab) => void;
  cartCount: number;
  /**
   * One-shot attention cue: bumps 0 → 1 when the user selects their first
   * photo(s), adding a class whose CSS animation pulses the Configure pill
   * once to draw the eye toward the next step.
   */
  configureCue?: number;
  photos: ReactNode;
  configure: ReactNode;
  cart: ReactNode;
}

export function MobileTabs({
  tab,
  onTab,
  cartCount,
  configureCue = 0,
  photos,
  configure,
  cart,
}: MobileTabsProps) {
  const tabs: Array<{ key: SpTab; label: string }> = [
    { key: 'photos', label: 'Photos' },
    { key: 'configure', label: 'Customise' },
    { key: 'cart', label: 'Cart' },
  ];
  return (
    <div className="sptabs">
      <div className="sptabs__bar" role="tablist" aria-label="Size & Paper sections">
        {tabs.map((t, i) => (
          <Fragment key={t.key}>
            {i > 0 && (
              <span className="sptabs__sep" aria-hidden="true">
                ›
              </span>
            )}
            <button
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`sptabs__tab${tab === t.key ? ' sptabs__tab--on' : ''}${
                t.key === 'configure' && configureCue > 0 ? ' sptabs__tab--cue' : ''
              }`}
              onClick={() => onTab(t.key)}
            >
              {t.label}
              {t.key === 'cart' && cartCount > 0 && (
                <span className="sptabs__badge">{cartCount}</span>
              )}
            </button>
          </Fragment>
        ))}
      </div>
      <div className="sptabs__body">
        {tab === 'photos' ? photos : tab === 'configure' ? configure : cart}
      </div>
    </div>
  );
}
